import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { portalApi } from "@/lib/portalApi";

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const onPortal = location.pathname.startsWith("/portal");

  const refresh = useCallback(async () => {
    try {
      const data = await portalApi.me();
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!onPortal) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    refresh();
    return undefined;
  }, [onPortal, refresh]);

  const logout = useCallback(async () => {
    try {
      await portalApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, setUser, refresh, logout, isAuthenticated: !!user }),
    [user, loading, refresh, logout]
  );

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used within PortalAuthProvider");
  return ctx;
}
